import React from 'react';
import { motion } from 'framer-motion';
import { BrainCircuit, Gauge, GitBranch, Layers3 } from 'lucide-react';
import personalData from '../../data/personal';
import { serviceFit } from '../../data/portfolio';

const principles = [
  {
    icon: Gauge,
    title: 'Speed with judgment',
    body: 'I use the fastest stack that can still carry the real workflow, security, and handoff needs.',
  },
  {
    icon: Layers3,
    title: 'Interfaces for repeat use',
    body: 'The work usually lands in daily operations, so screens need density, clarity, and low friction.',
  },
  {
    icon: BrainCircuit,
    title: 'AI where it earns trust',
    body: 'LLMs are strongest as review, scoring, research, and summary layers around human decisions.',
  },
  {
    icon: GitBranch,
    title: 'Systems over screens',
    body: 'I think through data, states, edge cases, roles, and automations before polishing the UI.',
  },
];

const About = () => {
  return (
    <section id="about" className="section-padding relative bg-[#1b1b22]">
      <div className="container-wrapper">
        <div className="grid gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.19, 1, 0.22, 1] }}
          >
            <p className="section-label mb-4">Positioning</p>
            <h2 className="max-w-xl text-3xl font-black leading-tight tracking-normal text-white sm:text-4xl md:text-5xl">
              One builder for the messy middle between idea and operating system.
            </h2>
            <div className="mt-6 max-w-xl space-y-4 text-sm leading-7 text-slate-400 sm:text-base">
              {personalData.aboutMe.split('\n\n').map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
          </motion.div>

          <div className="grid gap-4 sm:grid-cols-2">
            {principles.map((item, index) => (
              <motion.article
                key={item.title}
                className="card p-5"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: index * 0.05 }}
              >
                <item.icon className="h-5 w-5 text-emerald-300" />
                <h3 className="mt-5 text-base font-semibold text-white">{item.title}</h3>
                <p className="mt-3 text-sm leading-6 text-slate-400">{item.body}</p>
              </motion.article>
            ))}
          </div>
        </div>

        <div className="mt-16 grid gap-4 lg:grid-cols-3">
          {serviceFit.map((fit, index) => (
            <motion.article
              key={fit.title}
              className="border-t border-white/10 pt-5"
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: index * 0.06 }}
            >
              <p className="text-lg font-semibold leading-snug text-white">{fit.title}</p>
              <p className="mt-3 text-sm leading-6 text-slate-400">{fit.detail}</p>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default About;
